"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Flip } from "gsap/dist/Flip";
import { useIsMobile, useIsPortraitExperience, useIsTabletPortrait, useHasMounted } from "./hooks";
import Button from "./components/Button";
import Equipo from "./components/Equipo";
import EquipoMobile from "./components/EquipoMobile";
import ContactPanel from "./components/ContactPanel";
import Scroll from "./components/Scroll";
import { useContent } from "./content/current";
import LogoCarousel from "./components/LogoCarousel";
import ModulosCarousel from "./components/ModulosCarousel";

const DeferredWebGl = dynamic(() => import("./components/WebGl/index_f"), {
  ssr: false,
});

const DeferredWebGlMobile = dynamic(
  () => import("./components/WebGlMobile/index_f"),
  {
    ssr: false,
  },
);

const DeferredWebGlTablet = dynamic(
  () => import("./components/WebGlTablet/index_f"),
  {
    ssr: false,
  },
);

const SectorScene = dynamic(() => import("./components/WebGl/SectorScene"), {
  ssr: false,
});

gsap.registerPlugin(ScrollTrigger, Flip);
// En tablets (iPad/Android) el toolbar del navegador se oculta/muestra al
// scrollear, cambiando innerHeight y disparando un resize->refresh de
// ScrollTrigger a mitad de gesto. Con triggers en vh (ej. el fade-in del
// cubo en #hero) eso recalcula start/end con la nueva altura y hace que
// los planos parpadeen/desaparezcan al frenar o volver un poco atras.
ScrollTrigger.config({ ignoreMobileResize: true });

const SECTOR3D_MARGIN_TOPS = ["0", "3vw", "4vw"];


const MODULO_GRAND_LABEL_GAP = 14;

const clampModuloGrandLabelCenter = (x, y, width, height) => ({
  x: Math.min(Math.max(x, width / 2), window.innerWidth - width / 2),
  y: Math.min(Math.max(y, height / 2), window.innerHeight - height / 2),
});

const createModuloGrandLabelRect = (x, y, width, height) => ({
  left: x - width / 2,
  right: x + width / 2,
  top: y - height / 2,
  bottom: y + height / 2,
});

const moduloGrandRectsOverlap = (a, b) =>
  !(
    a.right + MODULO_GRAND_LABEL_GAP <= b.left ||
    a.left >= b.right + MODULO_GRAND_LABEL_GAP ||
    a.bottom + MODULO_GRAND_LABEL_GAP <= b.top ||
    a.top >= b.bottom + MODULO_GRAND_LABEL_GAP
  );

const resolveModuloGrandLabelPositions = (
  labels,
  nodesById,
  previousSlotById = {},
) => {
  const placedRects = [];
  const nextSlotById = {};

  const resolved = labels
    .map((label, index) => {
      const node = nodesById[label.id];
      if (!node) return null;

      const rect = node.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      if (!width || !height) return null;

      const horizontalStep = width * 0.7 + MODULO_GRAND_LABEL_GAP;
      const verticalStep = height * 0.95 + MODULO_GRAND_LABEL_GAP;
      const diagonalStepX = width * 0.45 + MODULO_GRAND_LABEL_GAP;
      const diagonalStepY = height * 0.82 + MODULO_GRAND_LABEL_GAP;
      const bias = index % 2 === 0 ? 1 : -1;
      const preferredCandidateIndex = previousSlotById[label.id] ?? 0;

      const candidateOffsets = [
        { dx: 0, dy: 0 },
        { dx: bias * horizontalStep, dy: 0 },
        { dx: -bias * horizontalStep, dy: 0 },
        { dx: 0, dy: -verticalStep },
        { dx: 0, dy: verticalStep },
        { dx: bias * diagonalStepX, dy: -diagonalStepY },
        { dx: -bias * diagonalStepX, dy: -diagonalStepY },
        { dx: bias * diagonalStepX, dy: diagonalStepY },
        { dx: -bias * diagonalStepX, dy: diagonalStepY },
        { dx: 0, dy: -verticalStep * 1.8 },
        { dx: 0, dy: verticalStep * 1.8 },
        { dx: bias * horizontalStep * 1.35, dy: -verticalStep * 0.75 },
        { dx: -bias * horizontalStep * 1.35, dy: verticalStep * 0.75 },
      ];
      const orderedCandidates = [
        {
          ...candidateOffsets[preferredCandidateIndex],
          candidateIndex: preferredCandidateIndex,
        },
        ...candidateOffsets
          .map((candidate, candidateIndex) => ({
            ...candidate,
            candidateIndex,
          }))
          .filter(
            ({ candidateIndex }) => candidateIndex !== preferredCandidateIndex,
          ),
      ];

      let bestCandidate = null;

      orderedCandidates.forEach(
        ({ dx, dy, candidateIndex }, candidateOrderIndex) => {
          const clampedCenter = clampModuloGrandLabelCenter(
            label.x + dx,
            label.y + dy,
            width,
            height,
          );
          const candidateRect = createModuloGrandLabelRect(
            clampedCenter.x,
            clampedCenter.y,
            width,
            height,
          );
          const overlapCount = placedRects.reduce(
            (count, placedRect) =>
              count +
              (moduloGrandRectsOverlap(candidateRect, placedRect) ? 1 : 0),
            0,
          );
          const displacement = Math.hypot(
            clampedCenter.x - label.x,
            clampedCenter.y - label.y,
          );
          const preferredSlotPenalty =
            candidateIndex === preferredCandidateIndex ? 0 : 18;
          const score =
            overlapCount * 100000 +
            displacement +
            preferredSlotPenalty +
            candidateOrderIndex * 0.01;

          if (!bestCandidate || score < bestCandidate.score) {
            bestCandidate = {
              x: clampedCenter.x,
              y: clampedCenter.y,
              rect: candidateRect,
              candidateIndex,
              score,
            };
          }
        },
      );

      if (!bestCandidate) return null;

      placedRects.push(bestCandidate.rect);
      nextSlotById[label.id] = bestCandidate.candidateIndex;

      return {
        id: label.id,
        x: bestCandidate.x,
        y: bestCandidate.y,
      };
    })
    .filter(Boolean);

  return {
    labels: resolved,
    nextSlotById,
  };
};

export default function Home() {
  const content = useContent();
  const HOME = content.home;
  const SECTORS = HOME.solutions.sectors;
  const MODULE_ITEMS = content.modules;
  const MODULO_GRAND_GROUPS = HOME.modular.groups;
  const TAG_ICON_SRC = useMemo(() => {
    const map = new Map(MODULE_ITEMS.map((item) => [item.label, item.iconSrc]));
    map.set("Insights", map.get("Insight"));
    return map;
  }, [MODULE_ITEMS]);
  const [contactOpen, setContactOpen] = useState(false);
  const [activeSector, setActiveSector] = useState(0);

  const [solWrapperHeight, setSolWrapperHeight] = useState(null);
  const sectorFadeRef = useRef(null);
  const [shouldLoadWebGl, setShouldLoadWebGl] = useState(false);
  const [webGlVisible, setWebGlVisible] = useState(false);
  const [hoveredModuloIndex, setHoveredModuloIndex] = useState(null);
  const [hoveredModuloGrandIndex, setHoveredModuloGrandIndex] = useState(null);
  const [activeModuloGrandScrollIndex, setActiveModuloGrandScrollIndex] = useState(null);
  const lockedModuloGrandIndexRef = useRef(null);
  const lockedModuloIndexRef = useRef(null);
  const [areModuloButtonsInteractive, setAreModuloButtonsInteractive] =
    useState(true);
  const isMobile = useIsMobile();
  const isPortraitExperience = useIsPortraitExperience();
  const isTabletPortrait = useIsTabletPortrait();
  const hasMounted = useHasMounted();
  // r3f's reconciler crashes (React #177 "Cannot commit the same tree") if a
  // <Canvas> is unmounted and another mounted in the same tick on resize. Gate
  // the variant: tear the current Canvas down to null, then mount the new one
  // on the next frame so the two commits never overlap.
  const webGlVariant = isMobile
    ? "mobile"
    : isTabletPortrait
      ? "tablet"
      : "desktop";
  const [mountedWebGlVariant, setMountedWebGlVariant] = useState(webGlVariant);
  const webGlVariantRef = useRef(webGlVariant);
  useEffect(() => {
    if (webGlVariantRef.current === webGlVariant) return;
    webGlVariantRef.current = webGlVariant;
    setMountedWebGlVariant(null);
    const id = requestAnimationFrame(() => setMountedWebGlVariant(webGlVariant));
    return () => cancelAnimationFrame(id);
  }, [webGlVariant]);

  // El contenido arranca en ES y cambia a EN tras montar (useContent). Ese swap
  // altera alturas y deja los ScrollTrigger medidos contra el DOM viejo, asi que
  // refrescamos para recalcular start/end con el texto definitivo.
  useEffect(() => {
    const id = requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => cancelAnimationFrame(id);
  }, [content]);
  // Al cruzar a otra variante de layout (mobile / tablet / desktop) recargamos
  // la pagina para montar esa version desde cero. La base se lee de matchMedia
  // real al montar (no del estado de los hooks, que arrancan en false y
  // dispararian un bucle de recargas). Debounce de 250ms para el arrastre.
  useEffect(() => {
    const variantNow = () => {
      if (window.matchMedia("(max-width: 767px)").matches) return "mobile";
      if (
        window.matchMedia(
          "(min-width: 769px) and (max-width: 1200px) and (orientation: portrait)",
        ).matches
      )
        return "tablet";
      return "desktop";
    };
    const baseline = variantNow();
    let timer;
    const onResize = () => {
      if (variantNow() === baseline) return;
      clearTimeout(timer);
      timer = setTimeout(() => window.location.reload(), 250);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  const moduloDescRef = useRef(null);
  const moduloGrandWordsRef = useRef({});
  const lastModuloAnchorRef = useRef(null);
  const lastModuloDescPositionRef = useRef({
    left: null,
    top: null,
    visible: false,
  });
  const lastModuloGrandWordStylesRef = useRef({});
  const lastModuloGrandProjectedLabelsRef = useRef(null);
  const moduloGrandLabelSlotsRef = useRef({});
  const moduloGrandLabelAnimationStateRef = useRef({});
  const moduloGrandLabelAnimationFrameRef = useRef(null);
  const moduloGrandLabelVisibleIdsRef = useRef(new Set());

  const clearModuloHovers = useCallback(() => {
    if (lockedModuloIndexRef.current === null) {
      setHoveredModuloIndex(null);
    }
    if (lockedModuloGrandIndexRef.current === null) {
      setHoveredModuloGrandIndex(null);
    }
  }, []);

  const clearModuloHover = useCallback(() => {
    if (lockedModuloIndexRef.current !== null) return;
    setHoveredModuloIndex(null);
  }, []);

  const clearModuloGrandHover = useCallback(() => {
    if (lockedModuloGrandIndexRef.current !== null) return;
    setHoveredModuloGrandIndex(null);
  }, []);


  const handleModuloGrandClick = useCallback((index) => {
    lockedModuloGrandIndexRef.current = index;
    setHoveredModuloIndex(null);
    setHoveredModuloGrandIndex(index);
  }, []);

  const handleModuloClick = useCallback((index) => {
    lockedModuloIndexRef.current = index;
    lockedModuloGrandIndexRef.current = null;
    setHoveredModuloGrandIndex(null);
    setHoveredModuloIndex(index);
  }, []);
  const handleWebGlReady = useCallback(() => {
    setWebGlVisible(true);
  }, []);

  const hideModuloDescription = useCallback(() => {
    const moduloDesc = moduloDescRef.current;
    if (!moduloDesc) return;

    moduloDesc.style.opacity = "0";
    lastModuloDescPositionRef.current.visible = false;
  }, []);

  const positionModuloDescription = useCallback(
    (anchor) => {
      lastModuloAnchorRef.current = anchor;

      const moduloDesc = moduloDescRef.current;
      if (!moduloDesc || !anchor) {
        hideModuloDescription();
        return;
      }

      const rect = moduloDesc.getBoundingClientRect();
      const left = anchor.x;
      const top = anchor.y - rect.height;

      const nextLeft = `${Math.round(left)}px`;
      const nextTop = `${Math.round(top)}px`;
      const lastPosition = lastModuloDescPositionRef.current;

      if (lastPosition.left !== nextLeft) {
        moduloDesc.style.left = nextLeft;
        lastPosition.left = nextLeft;
      }

      if (lastPosition.top !== nextTop) {
        moduloDesc.style.top = nextTop;
        lastPosition.top = nextTop;
      }

      if (!lastPosition.visible) {
        moduloDesc.style.opacity = "1";
        lastPosition.visible = true;
      }
    },
    [hideModuloDescription],
  );

  const handleActiveStackAnchorChange = useCallback(
    (anchor) => {
      positionModuloDescription(anchor);
    },
    [positionModuloDescription],
  );

  const animateModuloGrandLabels = useCallback(() => {
    let shouldContinue = false;

    moduloGrandLabelVisibleIdsRef.current.forEach((id) => {
      const node = moduloGrandWordsRef.current[id];
      const animationState = moduloGrandLabelAnimationStateRef.current[id];
      if (!node || !animationState) return;

      if (animationState.currentX == null || animationState.currentY == null) {
        animationState.currentX = animationState.targetX;
        animationState.currentY = animationState.targetY;
      } else {
        animationState.currentX +=
          (animationState.targetX - animationState.currentX) * 0.16;
        animationState.currentY +=
          (animationState.targetY - animationState.currentY) * 0.16;

        if (
          Math.abs(animationState.targetX - animationState.currentX) > 0.35 ||
          Math.abs(animationState.targetY - animationState.currentY) > 0.35
        ) {
          shouldContinue = true;
        } else {
          animationState.currentX = animationState.targetX;
          animationState.currentY = animationState.targetY;
        }
      }

      const nextTransform =
        `translate3d(${animationState.currentX.toFixed(2)}px, ${animationState.currentY.toFixed(2)}px, 0) ` +
        `translate(-50%, -50%)`;
      const lastStyles = lastModuloGrandWordStylesRef.current[id];

      if (lastStyles?.transform !== nextTransform) {
        node.style.transform = nextTransform;
      }

      if (lastStyles?.opacity !== "1") {
        node.style.opacity = "1";
      }

      lastModuloGrandWordStylesRef.current[id] = {
        transform: nextTransform,
        opacity: "1",
      };
    });

    if (shouldContinue) {
      moduloGrandLabelAnimationFrameRef.current = window.requestAnimationFrame(
        animateModuloGrandLabels,
      );
      return;
    }

    moduloGrandLabelAnimationFrameRef.current = null;
  }, []);

  const ensureModuloGrandLabelAnimation = useCallback(() => {
    if (moduloGrandLabelAnimationFrameRef.current !== null) return;
    moduloGrandLabelAnimationFrameRef.current = window.requestAnimationFrame(
      animateModuloGrandLabels,
    );
  }, [animateModuloGrandLabels]);

  const hideModuloGrandLabels = useCallback(() => {
    if (moduloGrandLabelAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(moduloGrandLabelAnimationFrameRef.current);
      moduloGrandLabelAnimationFrameRef.current = null;
    }

    lastModuloGrandProjectedLabelsRef.current = null;
    moduloGrandLabelVisibleIdsRef.current = new Set();
    moduloGrandLabelAnimationStateRef.current = {};
    moduloGrandLabelSlotsRef.current = {};

    Object.values(moduloGrandWordsRef.current).forEach((node) => {
      if (!node) return;
      node.style.opacity = "0";
    });
    lastModuloGrandWordStylesRef.current = {};
  }, []);

  const applyModuloGrandLabels = useCallback(
    (labels) => {
      lastModuloGrandProjectedLabelsRef.current = Array.isArray(labels)
        ? labels
        : null;

      const { labels: resolvedLabels, nextSlotById } = Array.isArray(labels)
        ? resolveModuloGrandLabelPositions(
            labels,
            moduloGrandWordsRef.current,
            moduloGrandLabelSlotsRef.current,
          )
        : { labels: [], nextSlotById: {} };
      const visibleIds = new Set(resolvedLabels.map(({ id }) => id));

      moduloGrandLabelVisibleIdsRef.current = visibleIds;
      moduloGrandLabelSlotsRef.current = nextSlotById;

      Object.entries(moduloGrandWordsRef.current).forEach(([id, node]) => {
        if (!node) return;
        if (!visibleIds.has(id)) {
          node.style.opacity = "0";
          delete lastModuloGrandWordStylesRef.current[id];
          delete moduloGrandLabelAnimationStateRef.current[id];
          delete moduloGrandLabelSlotsRef.current[id];
        }
      });

      if (!resolvedLabels.length) return;

      resolvedLabels.forEach(({ id, x, y }) => {
        const node = moduloGrandWordsRef.current[id];
        if (!node) return;

        const animationState = moduloGrandLabelAnimationStateRef.current[id];
        if (!animationState) {
          moduloGrandLabelAnimationStateRef.current[id] = {
            currentX: x,
            currentY: y,
            targetX: x,
            targetY: y,
          };
        } else {
          animationState.targetX = x;
          animationState.targetY = y;
        }

        node.style.opacity = "1";
      });

      ensureModuloGrandLabelAnimation();
    },
    [ensureModuloGrandLabelAnimation],
  );

  const handleModuloGrandLabelsChange = useCallback(
    (labels) => {
      applyModuloGrandLabels(labels);
    },
    [applyModuloGrandLabels],
  );

  const handleModuloButtonsInteractivityChange = useCallback(
    (isInteractive) => {
      setAreModuloButtonsInteractive((current) =>
        current === isInteractive ? current : isInteractive,
      );
    },
    [],
  );

  const activeModuloItem = Number.isInteger(hoveredModuloIndex)
    ? (MODULE_ITEMS[hoveredModuloIndex] ?? null)
    : null;
  const activeModuloGrandGroup = Number.isInteger(hoveredModuloGrandIndex)
    ? (MODULO_GRAND_GROUPS[hoveredModuloGrandIndex] ?? null)
    : null;

  useEffect(() => {
    if (areModuloButtonsInteractive) return;
    clearModuloHover();
  }, [areModuloButtonsInteractive, clearModuloHover]);

  useEffect(() => {
    if (isMobile) return;
    const el = sectorFadeRef.current;
    if (!el) return;
    const measure = () => {
      if (!el.isConnected) return;
      const contentHeight = el.offsetHeight;
      if (contentHeight === 0) return;
      const paddingPx = (1.5 / 100) * window.innerWidth;
      const next = contentHeight + paddingPx * 2;
      setSolWrapperHeight((prev) =>
        prev !== null && Math.abs(prev - next) < 0.5 ? prev : next,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [activeSector, isMobile]);

  useLayoutEffect(() => {
    if (!activeModuloItem) {
      hideModuloDescription();
      return;
    }

    const expectedAnchorKey = `plate-extra-${hoveredModuloIndex + 1}`;
    if (lastModuloAnchorRef.current?.key !== expectedAnchorKey) {
      hideModuloDescription();
      return;
    }

    positionModuloDescription(lastModuloAnchorRef.current);
  }, [
    activeModuloItem,
    hideModuloDescription,
    hoveredModuloIndex,
    positionModuloDescription,
  ]);

  useEffect(() => {
    if (activeModuloGrandGroup) return;
    hideModuloGrandLabels();
  }, [activeModuloGrandGroup, hideModuloGrandLabels]);

  useLayoutEffect(() => {
    if (!activeModuloGrandGroup) return;
    if (!lastModuloGrandProjectedLabelsRef.current) return;

    applyModuloGrandLabels(lastModuloGrandProjectedLabelsRef.current);
  }, [activeModuloGrandGroup, applyModuloGrandLabels]);

  useEffect(
    () => () => {
      if (moduloGrandLabelAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(moduloGrandLabelAnimationFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    let idleId = null;

    const enableWebGl = () => {
      if (cancelled) return;
      setShouldLoadWebGl(true);
    };

    // Diferir el montaje de la escena 3D hasta despues del load + idle del
    // navegador, para sacar Three.js de la ruta critica (LCP/TTI).
    const scheduleAfterIdle = () => {
      if (cancelled) return;
      if ("requestIdleCallback" in window) {
        idleId = window.requestIdleCallback(enableWebGl, { timeout: 2000 });
      } else {
        idleId = window.setTimeout(enableWebGl, 200);
      }
    };

    let loadHandler = null;
    if (document.readyState === "complete") {
      scheduleAfterIdle();
    } else {
      loadHandler = scheduleAfterIdle;
      window.addEventListener("load", loadHandler, { once: true });
    }

    return () => {
      cancelled = true;
      if (loadHandler) window.removeEventListener("load", loadHandler);
      if (idleId !== null) {
        if ("cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
        window.clearTimeout(idleId);
      }
    };
  }, []);

  useEffect(() => {
    let pointerX = null;
    let pointerY = null;

    const updateHoversFromPointer = (x, y) => {
      const hoveredElement = document.elementFromPoint(x, y);
      const hoveredModulo = hoveredElement?.closest(".modulo");
      const hoveredModuloGrand = hoveredElement?.closest(".modulogrand");

      if (lockedModuloIndexRef.current === null) {
        if (hoveredModulo) {
          const nextModuloIndex = Number(
            hoveredModulo.getAttribute("data-modulo-index"),
          );
          if (Number.isInteger(nextModuloIndex)) {
            setHoveredModuloIndex((current) =>
              current === nextModuloIndex ? current : nextModuloIndex,
            );
          }
        } else {
          setHoveredModuloIndex((current) => (current === null ? current : null));
        }
      }

      if (lockedModuloGrandIndexRef.current === null && !isTabletPortrait) {
        if (hoveredModuloGrand) {
          const nextModuloGrandIndex = Number(
            hoveredModuloGrand.getAttribute("data-modulogrand-index"),
          );
          if (Number.isInteger(nextModuloGrandIndex)) {
            setHoveredModuloGrandIndex((current) =>
              current === nextModuloGrandIndex ? current : nextModuloGrandIndex,
            );
          }
        } else {
          setHoveredModuloGrandIndex((current) =>
            current === null ? current : null,
          );
        }
      }
    };

    let pointerRafId = null;
    const schedulePointerUpdate = () => {
      if (pointerRafId !== null) return;
      pointerRafId = requestAnimationFrame(() => {
        pointerRafId = null;
        if (pointerX === null || pointerY === null) return;
        updateHoversFromPointer(pointerX, pointerY);
      });
    };

    const handlePointerMove = (event) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      schedulePointerUpdate();
    };

    const handleScroll = () => {
      const modularSection = document.getElementById("modular");
      const rect = modularSection?.getBoundingClientRect();
      const isOutsideModularView =
        !rect || rect.bottom <= 0 || rect.top >= window.innerHeight;

      if (isOutsideModularView) {
        lockedModuloGrandIndexRef.current = null;
        lockedModuloIndexRef.current = null;
        clearModuloHovers();
        return;
      }

      if (pointerX === null || pointerY === null) {
        clearModuloHovers();
        return;
      }
      schedulePointerUpdate();
    };

    const handlePointerOut = (event) => {
      if (event.relatedTarget) return;
      clearModuloHovers();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearModuloHovers();
      }
    };


    const handlePointerDown = (event) => {
      if (event.target?.closest?.(".modulogrand, .modulo")) return;
      lockedModuloIndexRef.current = null;
      lockedModuloGrandIndexRef.current = null;
      setHoveredModuloIndex(null);
      setHoveredModuloGrandIndex(null);
    };
    const hasHover = !window.matchMedia("(hover: none)").matches;

    if (hasHover) {
      window.addEventListener("pointermove", handlePointerMove, {
        passive: true,
      });
      window.addEventListener("pointerout", handlePointerOut);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("blur", clearModuloHovers);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      if (pointerRafId !== null) cancelAnimationFrame(pointerRafId);
      if (hasHover) {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerout", handlePointerOut);
      }
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("blur", clearModuloHovers);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [clearModuloHovers, isTabletPortrait]);

  useLayoutEffect(() => {
    const section = document.getElementById("carruselhor");
    const inner = document.getElementById("carruselhorin");
    if (!section || !inner) return undefined;
    const HORIZONTAL_TRAVEL_VW = 280;
    const CARRUSEL_SCROLL_DURATION_FACTOR = 0.5;
    const isLandscape = window.matchMedia(
      "(min-width: 769px) and (orientation: landscape)",
    ).matches;
    const getTravelPixels = () =>
      (window.innerWidth *
        HORIZONTAL_TRAVEL_VW *
        CARRUSEL_SCROLL_DURATION_FACTOR) /
      100;

    const ctx = gsap.context(() => {
      gsap.set(inner, { x: 0 });

      if (!isMobile) {
        gsap.to(inner, {
          x: isLandscape ? `-${HORIZONTAL_TRAVEL_VW}vw` : 0,
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: () => `+=${getTravelPixels()}`,
            scrub: true,
            pin: isLandscape,
            invalidateOnRefresh: true,
            snap: isLandscape
              ? {
                  snapTo: 1 / 5,
                  duration: { min: 0.1, max: 0.3 },
                  ease: "power1.inOut",
                  delay: 0,
                }
              : undefined,
            onRefresh: (self) => {
              if (!isLandscape) return;
              if (!self.isActive) return;
              const snapped = Math.round(self.progress * 5) / 5;
              window.scrollTo(
                0,
                self.start + (self.end - self.start) * snapped,
              );
            },
          },
        });
      }
    });

    return () => {
      try {
        ctx.revert();
      } catch (_) {
        // GSAP pin spacer removeChild races with React DOM cleanup — safe to ignore
        ctx.kill();
      }
    };
  }, [isMobile]);

  useEffect(() => {
    const items = document.querySelectorAll(".procesoitem");
    if (!items.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("is-active", entry.isIntersecting);
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: "0px 0px -30% 0px",
      },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [HOME.process.items]);

  useEffect(() => {
    const trigger = document.getElementById("productividadgap");
    const square = document.getElementById("squareanimin");
    if (!trigger || !square) return undefined;

    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger,
        start: "top 20%",
        end: "top -30%",
        scrub: true,
        invalidateOnRefresh: true,
      },
    });

    timeline.to(
      square,
      {
        opacity: 1,
        ease: "none",
        duration: 0.05,
      },
      0,
    );

    timeline.to(
      square,
      {
        width: "100vw",
        height: "100vh",
        borderRadius: 0,
        ease: "power1.out",
        duration: 0.95,
      },
      0.08,
    );

    return () => {
      timeline.scrollTrigger?.kill();
      timeline.kill();
    };
  }, []);

  useEffect(() => {
    const trigger = document.getElementById("productividad");
    const squareAnim = document.getElementById("squareanim");
    if (!trigger || !squareAnim) return undefined;

    gsap.set(squareAnim, { opacity: 1 });

    const tween = gsap.to(squareAnim, {
      opacity: 0,
      ease: "none",
      scrollTrigger: {
        trigger,
        start: "top -1%",
        end: "top -2%",
        scrub: true,
        invalidateOnRefresh: true,
      },
    });

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
      gsap.set(squareAnim, { opacity: 1 });
    };
  }, []);

  useEffect(() => {
    const productividad = document.getElementById("productividad");
    const productividadTitle = productividad?.querySelector("h3");
    const bulletsContainer = document.getElementById("productividadbullets");
    const bullets = bulletsContainer?.querySelectorAll(".bullet");

    if (
      !productividad ||
      !productividadTitle ||
      !bulletsContainer ||
      !bullets?.length
    ) {
      return undefined;
    }

    const TITLE_SCROLL_FRACTION = 0.03;
    const BULLETS_SCROLL_FRACTION = 0.18;

    const ctx = gsap.context(() => {
      gsap.set(productividadTitle, { opacity: 0 });

      gsap.to(productividadTitle, {
        opacity: 1,
        ease: "none",
        scrollTrigger: {
          trigger: productividad,
          start: "top 50%",
          end: () => `+=${window.innerHeight * TITLE_SCROLL_FRACTION}`,
          scrub: true,
          invalidateOnRefresh: true,
        },
      });

      gsap.set(bullets, { opacity: 0 });
      gsap.to(bullets, {
        opacity: 1,
        ease: "none",
        stagger: 0.18,
        scrollTrigger: {
          trigger: bulletsContainer,
          start: "top 70%",
          end: () => `+=${window.innerHeight * BULLETS_SCROLL_FRACTION}`,
          scrub: true,
          invalidateOnRefresh: true,
        },
      });
    });

    return () => ctx.revert();
  }, [HOME.benefits.items]);

  useEffect(() => {
    if (
      window.matchMedia('(min-width: 769px) and (orientation: landscape)').matches
    )
      return undefined;

    const cards = document.querySelectorAll(".modulogrand");
    if (!cards.length) return undefined;

    const triggers = Array.from(cards).map((card, index) =>
      ScrollTrigger.create({
        trigger: card,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setActiveModuloGrandScrollIndex(index),
        onLeave: () => setActiveModuloGrandScrollIndex((prev) => (prev === index ? null : prev)),
        onEnterBack: () => setActiveModuloGrandScrollIndex(index),
        onLeaveBack: () => setActiveModuloGrandScrollIndex((prev) => (prev === index ? null : prev)),
      })
    );

    return () => {
      triggers.forEach((t) => t.kill());
    };
  }, [content]);


  return (
    <>
      <div id="degsup"></div>
      <div id="degbot"></div>
      <div id="squareanim">
        <div id="squareanimin"></div>
      </div>
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "#fff",
          opacity: webGlVisible ? 0 : 1,
          transition: "opacity 420ms ease",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <div id="webgl" className={webGlVisible ? "on" : ""}>
        {shouldLoadWebGl && mountedWebGlVariant === "mobile" ? (
            <DeferredWebGlMobile
              key="webgl-mobile"
              hoveredModuloIndex={hoveredModuloIndex}
              hoveredModuloGrandIndex={hoveredModuloGrandIndex}
              onActiveStackAnchorChange={handleActiveStackAnchorChange}
              onModuloGrandLabelsChange={handleModuloGrandLabelsChange}
              onModuloButtonsInteractivityChange={
                handleModuloButtonsInteractivityChange
              }
              onReady={handleWebGlReady}
            />
          ) : shouldLoadWebGl && mountedWebGlVariant === "tablet" ? (
            <DeferredWebGlTablet
              key="webgl-tablet"
              hoveredModuloIndex={hoveredModuloIndex}
              hoveredModuloGrandIndex={hoveredModuloGrandIndex}
              onActiveStackAnchorChange={handleActiveStackAnchorChange}
              onModuloGrandLabelsChange={handleModuloGrandLabelsChange}
              onModuloButtonsInteractivityChange={
                handleModuloButtonsInteractivityChange
              }
              onReady={handleWebGlReady}
            />
          ) : shouldLoadWebGl && mountedWebGlVariant === "desktop" ? (
            <DeferredWebGl
              key="webgl-desktop"
              hoveredModuloIndex={hoveredModuloIndex}
              hoveredModuloGrandIndex={hoveredModuloGrandIndex}
              onActiveStackAnchorChange={handleActiveStackAnchorChange}
              onModuloGrandLabelsChange={handleModuloGrandLabelsChange}
              onModuloButtonsInteractivityChange={
                handleModuloButtonsInteractivityChange
              }
              onReady={handleWebGlReady}
            />
          ) : null}
      </div>
      <div id="modulos1words" aria-hidden="true">
        {activeModuloGrandGroup
          ? activeModuloGrandGroup.moduleLabels.map((label) => (
              <div
                key={label}
                className="palabra"
                ref={(node) => {
                  if (node) {
                    moduloGrandWordsRef.current[label] = node;
                    return;
                  }

                  delete moduloGrandWordsRef.current[label];
                  delete lastModuloGrandWordStylesRef.current[label];
                }}
              >
                {label}
              </div>
            ))
          : null}
      </div>
      <Scroll locked={contactOpen}>
        <section id="hero">
          <h3>{HOME.hero.eyebrow}</h3>
          <h1>{HOME.hero.title}</h1>
          <p>{HOME.hero.body}</p>
          <Button onClick={() => setContactOpen(true)}>{HOME.hero.cta}</Button>
        </section>
        <section id="proceso">
          <h2>{HOME.process.title}</h2>
          {HOME.process.items.map((item, index) => (
            <div key={item} id={`proceso${index + 1}`} className="procesoitem">
              <div className="num">{String(index + 1).padStart(2, "0")}</div>
              <p>{item}</p>
            </div>
          ))}
        </section>
        <section id="carruselhor">
          <div id="carruselhorin">
            {HOME.marquee.map((item) => (
              <div className="item" key={item}><span>{item}</span></div>
            ))}
          </div>
        </section>
        <section id="modular" onPointerLeave={clearModuloHovers}>
          <h3>{HOME.modular.eyebrow}</h3>
          <h2>{HOME.modular.title}</h2>
          <p>{HOME.modular.body}</p>
          <Button onClick={() => setContactOpen(true)}>{HOME.modular.cta}</Button>
          <div id="modulos1" onPointerLeave={clearModuloGrandHover}>
            {MODULO_GRAND_GROUPS.map((group, i) => (
              <div
                key={group.title}
                className={`modulogrand ${
                  (isMobile || isTabletPortrait
                    ? activeModuloGrandScrollIndex === i
                    : hoveredModuloGrandIndex === i || activeModuloGrandScrollIndex === i)
                    ? "is-active"
                    : ""
                }`.trim()}
                data-modulogrand-index={i}
                onPointerEnter={() => {
                  if (isMobile || isTabletPortrait) return;
                  if (lockedModuloGrandIndexRef.current !== null) return;
                  setHoveredModuloIndex(null);
                  setHoveredModuloGrandIndex(i);
                }}
                onPointerLeave={isMobile || isTabletPortrait ? undefined : clearModuloGrandHover}
              >
                <h3>{group.title}</h3>
                {group.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                <div className="modulogrand-tags">
                  {group.moduleLabels.map((label) => (
                    <h3 key={label}>{label}</h3>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div id="modulos2">
            <div
              id="modulos2desc"
              ref={moduloDescRef}
              className={activeModuloItem ? "is-active" : ""}
            >
              {activeModuloItem ? (
                <>
                  <span className="modulos2desc-title">
                    {activeModuloItem.label}
                  </span>
                  {activeModuloItem.description}
                </>
              ) : null}
            </div>
            <h4>{HOME.modular.allModulesTitle}</h4>
            {isMobile || isPortraitExperience ? (
              <ModulosCarousel items={MODULE_ITEMS} />
            ) : (
              <div
                id="modulos2container"
                onPointerLeave={clearModuloHover}
                style={{
                  pointerEvents: areModuloButtonsInteractive
                    ? "auto"
                    : "none",
                }}
              >
                {MODULE_ITEMS.map((moduleItem, index) => (
                  <div
                    key={`modulo-${moduleItem.label}`}
                    className={`modulo ${hoveredModuloIndex === index ? "is-active" : ""}`.trim()}
                    data-modulo-index={index}
                    onPointerEnter={() => {
                      if (lockedModuloIndexRef.current !== null) return;
                      setHoveredModuloGrandIndex(null);
                      setHoveredModuloIndex(index);
                    }}
                    onPointerLeave={clearModuloHover}
                    onClick={() => handleModuloClick(index)}
                  >
                    <img
                      src={moduleItem.iconSrc}
                      alt={moduleItem.alt}
                      width="120"
                      height="120"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="modulotext">{moduleItem.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        <section id="productividadgap"></section>
        <section id="productividad">
          <h3>{HOME.benefits.eyebrow}</h3>
          <h1>{HOME.benefits.title}</h1>
          <div id="productividadbullets">
            {HOME.benefits.items.map((item) => (
              <div className="bullet" key={item}>{item}</div>
            ))}
          </div>
        </section>
        <div id="casos">
          <div id="casosin">
            {isMobile ? (
              HOME.cases.mobile
            ) : (
              <>
                <span>{HOME.cases.desktop}&nbsp;·&nbsp;</span>
                <span aria-hidden="true">{HOME.cases.desktop}&nbsp;·&nbsp;</span>
              </>
            )}
          </div>
        </div>
        <section id="soluciones">
          <h2>{HOME.solutions.title}</h2>
          <>
              <div id="selectores">
                {SECTORS.map((sector, i) => (
                  <div
                    key={sector.label}
                    className={`selector${activeSector === i ? " is-active" : ""}`}
                    onClick={() => setActiveSector(i)}
                  >
                    {sector.label}
                  </div>
                ))}
              </div>
              <div id="solucionescont">
                <div
                  className="sol-wrapper"
                  style={
                    solWrapperHeight
                      ? { height: `${solWrapperHeight}px` }
                      : undefined
                  }
                >
                  <div
                    className="sector-fade"
                    key={activeSector}
                    ref={sectorFadeRef}
                  >
                    <h2>
                      {SECTORS[activeSector].h2[0]}
                      <br />
                      {SECTORS[activeSector].h2[1]}
                    </h2>
                    {isMobile && (
                      <h3 className="sol-mobile-subtitle" key={activeSector}>
                        {SECTORS[activeSector].h3}
                      </h3>
                    )}
                    <ul id="tag-wrapper">
                      {SECTORS[activeSector].tags.map((tag) => (
                        <li key={tag.name} className="tag">
                          <img
                            src={
                              TAG_ICON_SRC.get(tag.name) ??
                              "/icons/soluciones.svg"
                            }
                            alt={tag.name}
                            width="64"
                            height="64"
                            decoding="async"
                          />
                          <div>
                            <p>
                              <strong>{tag.name}</strong>
                            </p>
                            <p>{tag.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div
                    id="sector3d"
                    style={{ marginTop: SECTOR3D_MARGIN_TOPS[activeSector] }}
                  >
                    <h3 key={activeSector}>{SECTORS[activeSector].h3}</h3>
                    <div id="webglsect">
                      <SectorScene activeSector={activeSector} cardCount={SECTORS[activeSector].tags.length} />
                    </div>
                  </div>
                </div>
              </div>
            </>
        </section>
        <LogoCarousel />
        {hasMounted &&
          (isPortraitExperience ? <EquipoMobile /> : <Equipo isMobile={isMobile} />)}
        <section id="final"></section>
      </Scroll>
      <div id="footerfix">
        <h3>{HOME.footer.eyebrow}</h3>
        <h1>{HOME.footer.title}</h1>
        <Button onClick={() => setContactOpen(true)}>{HOME.footer.cta}</Button>
        <div id="degfooter"></div>
        <nav id="footernav">
          <ul>
            <li className="link">
              <Link href="/politica-de-privacidad" prefetch={false}>{content.nav.privacy}</Link>
            </li>
            <li className="link">
              <Link href="/aviso-legal" prefetch={false}>{content.nav.legal}</Link>
            </li>
            <li className="link">
              <Link href="/politica-de-cookies" prefetch={false}>{content.nav.cookies}</Link>
            </li>
          </ul>
        </nav>
      </div>
      <ContactPanel
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </>
  );
}
